import type { PaletteMode, PaletteOptions } from '@mui/material';
import { type BrandPalette, graphite, indigo, lavender, palettes, status } from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    /** The mode's token set, so a component never picks a hex for itself. */
    brand: BrandPalette;
  }
  interface PaletteOptions {
    brand?: BrandPalette;
  }
}

/**
 * MUI's palette, filled from one token set per mode. Nothing here is a new
 * colour: every slot is a `palettes[mode]` value, a brand step or a status
 * tone. `secondary` is lavender — decoration, never an action.
 */
export function buildPalette(mode: PaletteMode): PaletteOptions {
  const light = mode === 'light';
  const brand = palettes[mode];
  return {
    mode,
    brand,
    primary: {
      main: brand.primary,
      light: light ? indigo.ink : lavender[200],
      dark: light ? indigo.pressed : lavender[500],
      contrastText: brand.onPrimary,
    },
    secondary: {
      main: brand.accent,
      light: light ? lavender.tint : lavender[300],
      dark: brand.accentInk,
      contrastText: light ? indigo.brand : brand.onPrimary,
    },
    // The approve act (Components board): white on teal, deep teal on aqua-teal.
    success: { main: brand.approve, contrastText: brand.onApprove },
    warning: { main: status.warning[mode], contrastText: light ? '#FFFFFF' : graphite.ground },
    error: { main: status.danger[mode], contrastText: light ? '#FFFFFF' : '#2B0B08' },
    info: { main: status.info[mode], contrastText: light ? '#FFFFFF' : brand.onPrimary },
    divider: brand.border,
    background: { default: brand.ground, paper: brand.panel },
    text: { primary: brand.ink, secondary: brand.inkMuted, disabled: brand.inkFaint },
    action: {
      hover: brand.hover,
      selected: brand.selected,
      focus: brand.focusRing,
      disabled: brand.disabledInk,
      disabledBackground: brand.disabledSurface,
    },
  };
}
